// ** MUI Imports
import { lighten, darken } from '@mui/material/styles';

// ** Type Import
import type { OwnerStateThemeType } from '.';
import type { Mode } from '../../types';

// ** Util Import
import { hexToRGBA } from '@erikdakoda/ui/shared/colorUtils';

const alertSeverities = ['success', 'info', 'warning', 'error'] as const;
type AlertSeverity = (typeof alertSeverities)[number];

/** Returns whether a value is a supported Alert severity palette key. */
function isAlertSeverity(value: unknown): value is AlertSeverity {
  return typeof value === 'string' && alertSeverities.includes(value as AlertSeverity);
}

/** Resolves the active severity from MUI Alert owner state. */
function getAlertSeverity(ownerState: OwnerStateThemeType['ownerState']): AlertSeverity | null {
  const severity = ownerState.colorSeverity ?? ownerState.severity;
  return isAlertSeverity(severity) ? severity : null;
}

const Alert = (mode: Mode) => {
  /** Dark mode needs a stronger lighten so success/warning/error stay clearly colored. */
  const getColor = (color: string): string =>
    mode === 'dark' ? lighten(color, 0.5) : darken(color, 0.12);

  /** MUI 9 tints `palette.*.light` heavily in dark mode; use main at higher alpha instead. */
  const getStandardBackground = (color: string): string =>
    hexToRGBA(color, mode === 'dark' ? 0.24 : 0.12);

  const sharedRootStyles = ({ theme }: OwnerStateThemeType) => ({
    borderRadius: 5,
    '& .MuiAlertTitle-root': {
      marginBottom: theme.spacing(1.6),
    },
    '& a': {
      fontWeight: 500,
      color: 'inherit',
    },
  });

  const severityTextStyles = (main: string) => ({
    color: getColor(main),
    '& .MuiAlertTitle-root': {
      color: getColor(main),
    },
    '& .MuiAlert-icon': {
      color: getColor(main),
    },
  });

  return {
    MuiAlert: {
      styleOverrides: {
        root: sharedRootStyles,
        message: () => ({
          width: '100%',
        }),
        standard: ({ theme, ownerState }: OwnerStateThemeType) => {
          const severity = getAlertSeverity(ownerState);
          if (!severity) {
            return {};
          }

          const main = theme.palette[severity].main;
          return {
            ...severityTextStyles(main),
            backgroundColor: getStandardBackground(main),
          };
        },
        outlined: ({ theme, ownerState }: OwnerStateThemeType) => {
          const severity = getAlertSeverity(ownerState);
          if (!severity) {
            return {};
          }

          const main = theme.palette[severity].main;
          return {
            ...severityTextStyles(main),
            borderColor: main,
          };
        },
        filled: ({ theme }: OwnerStateThemeType) => ({
          fontWeight: 400,
          color: theme.palette.common.white,
        }),
      },
    },
  };
};

export default Alert;
